import { Component, inject, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ManageCegViewMode,
  ManageTableViewConfigSections, TableConfig,
  TableConfigActionType, TableConfigDialog,
  TableConfigKeys
} from '@manage-ceg/types/manage-ceg-page.types';
import { LocalStorageService } from '@common-lib/services/local-storage.service';
import { TableConfigDialogService } from '../table-config-dialog/table-config-dialog.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { Subject, finalize, take, takeUntil } from 'rxjs';
import { CustomAvailableBudgetConfigurationService } from 'app/custom-available-budget/custom-available-budget-configuration.service';
import { defaultViewConfigForCAB } from '@manage-ceg/services/manage-ceg-table-configuration.service';


@Component({
  selector: 'custom-available-budget-table-config-dialog',
  templateUrl: './custom-available-budget-table-config-dialog.component.html',
  styleUrls: ['./custom-available-budget-table-config-dialog.component.scss']
})
export class CustomAvailableBudgetTableConfigDialogComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  private tableConfigDialogService: TableConfigDialogService = inject(TableConfigDialogService);
  private utilityService = inject(UtilityService);
  public tableActionType = TableConfigActionType;
  public cegViewMode = ManageCegViewMode;
  public tableConfigKeys = TableConfigKeys;
  public tableViewSections = ManageTableViewConfigSections;
  isForecastEnabled = LocalStorageService.getFromStorage('forecastEnabled');
  selectedFormulaItems = [];
  selectedRowItems = [];
  lastSavedSelectedColumnFormulaItems = [];
  lastSavedSelectedRowItems = [];
  hasManagePageFormulaChanged = false;
  disableResetForCAB = false;
  nonHighlightedFormulaItem = ['Total Budget', 'Available Budget'];


  private readonly customAvailableBudgetConfigurationService = inject(CustomAvailableBudgetConfigurationService);

  constructor(
    public dialogRef: MatDialogRef<CustomAvailableBudgetTableConfigDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TableConfigDialog
  ) { 
    this.initConfigureDialogState();
  }

  initConfigureDialogState(): void {
    this.selectedFormulaItems =  this.getSelectedCustomAvailableBudgetFormulaItems();
    this.selectedRowItems = this.getSelectedRowItems();
    this.handleDisabledChecboxStateForCustomAvailableBudgetTableConfigDialog();
    this.lastSavedSelectedColumnFormulaItems = this.selectedFormulaItems;
    this.lastSavedSelectedRowItems = this.selectedRowItems;
    this.hasManagePageFormulaChanged = false;
    this.disableResetForCAB = this.areBudgetFormulaAndManagePageSelectedCABFormulasEqual();
    if(!this.isForecastEnabled) {
      this.nonHighlightedFormulaItem = ['Total Budget', 'Actual Spend', 'Available Budget'];
    }
    if(this.data.isForecastSmartCell){
      this.nonHighlightedFormulaItem = ['Available Budget'];
    }
  }

  onSelectionChange(item: TableConfig, value: boolean, groupKey: ManageTableViewConfigSections): void {
    item.value = value;
    this.selectedFormulaItems = this.getSelectedCustomAvailableBudgetFormulaItems();
    this.selectedRowItems = this.getSelectedRowItems();
    if (item.type === TableConfigActionType.checkbox && groupKey === ManageTableViewConfigSections.columns) {
      this.handleDisabledChecboxStateForCustomAvailableBudgetTableConfigDialog();
      this.disableResetForCAB = this.areBudgetFormulaAndManagePageSelectedCABFormulasEqual();
    }
    if (groupKey === ManageTableViewConfigSections.segmentBreakdownData && item.type === TableConfigActionType.checkbox) {
      this.checkFormulaAllow(this.data.config[groupKey] as Array<TableConfig>);
    }

    this.checkIfManagePageFormulaChangedInConfigureTable();
  
  }


  onApply() {
      this.checkManagePageFormulaSameAsBudgetFormulaOnApply();
      this.onConfigChangesApplyNew();
      this.customAvailableBudgetConfigurationService.reloadRows()  // Reload rows to apply new config
    }

    onConfigChangesApplyNew() {
      const viewConfig = { segmentBreakdownData: {}, columns: {}, columnsFormula: false };
      Object.keys(this.data.config).forEach(rootKey => {
        const result = {};
        if (Array.isArray(this.data.config[rootKey])) {
          this.data.config[rootKey].forEach(item => {
            const { key, value } = item;
            result[key] = value || false;
            if (key === TableConfigKeys.columnsFormula) {
              viewConfig[key] = value || false;
            }
          });
          viewConfig[rootKey] = result;
        }
      });
  
      if (viewConfig[ManageTableViewConfigSections.columns][TableConfigKeys.separate]) {
        viewConfig[ManageTableViewConfigSections.columns][TableConfigKeys.committedAndPlanned] = false;
      }
  
      this.tableConfigDialogService.postUserTableConfig(
        this.data.companyId,
        this.data.budgetId,
        viewConfig
      ).pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          // this.utilityService.showLoading(false)
        })
      ).subscribe({
        next: data => {
          this.data.onSubmit(viewConfig);
          this.dialogRef.close();
        },
        error: error => {
          this.utilityService.showToast(
            { Title: '', Message: 'Please retry' }
          );
          console.error(error);
        }
      });
    }
  

  checkFormulaAllow(configSection: Array<TableConfig>): void {
    const formulaKeys = [TableConfigKeys.formula]
    
    const condition = configSection
      .filter(configType => configType.type === TableConfigActionType.checkbox && configType.key !== TableConfigKeys.forecast)
      .some(configType => !configType.value);
    
    const formula = configSection.find(configType => formulaKeys.includes(configType.key));
    
    if (condition) {
      formula.value = false;
      formula.disabled = true;
    } else {
      formula.disabled = false;
    }

  }

  handleDisabledChecboxStateForCustomAvailableBudgetTableConfigDialog(onReset = false): void {
    const columns = this.data.config.columns;

    if (onReset) {
      columns.forEach(item => item.disabled = false);
    }

    const total = columns.find(col => col.key === TableConfigKeys.budget);
    const actual = columns.find(col => col.key === TableConfigKeys.actual);
    const forecast = columns.find(col => col.key === TableConfigKeys.forecast);
    const committed = columns.find(col => col.key === TableConfigKeys.committed);
    const planned = columns.find(col => col.key === TableConfigKeys.planned);

    // Always disable Total Budget
    if (total && !this.data.isForecastSmartCell) total.disabled = true;

    // Get selected operand keys
    let selected = columns
      .filter(col => col.type === TableConfigActionType.checkbox && col.visible && col.value)
      .map(col => col.key);

    let isTotal = selected.includes(TableConfigKeys.budget);
    let isPlanned = selected.includes(TableConfigKeys.planned);
    let isCommitted = selected.includes(TableConfigKeys.committed);
    let isActual = selected.includes(TableConfigKeys.actual);
    let isForecast = selected.includes(TableConfigKeys.forecast);

    if(!this.data.isForecastSmartCell){
      const onlyTwoSelected = selected.length === 2;

      // --- Rule 1: Actual is forced and Forecast is disabled if Planned or Committed is selected
      const shouldForceActual = isPlanned || isCommitted;
      const shouldDisableForecast = shouldForceActual;

      if (shouldForceActual) {
        actual.value = true;
        actual.disabled = shouldForceActual;
        isActual = true;
      }
      else {
        actual.disabled = false;
      }

      if (forecast) {
        forecast.value = shouldDisableForecast ? false : forecast.value;
        forecast.disabled = shouldDisableForecast;
        isForecast = forecast.value;
      }

      // --- Rule 2: If only 2 operands are selected → disable both
      if (onlyTwoSelected) {
        columns.forEach(col => {
          if (selected.includes(col.key)) {
            col.disabled = true;
          }
        });
      }

      // --- Rule 3: Actual + Planned without Committed → force Committed selected + disabled
      const shouldForceCommitted = isActual && isPlanned && !isCommitted;
      const shouldDisableCommitted = isActual && isPlanned && isCommitted;

      if (shouldForceCommitted) {
        setTimeout(() => {
            committed.value = true;
            committed.disabled = true;
            selected.push(TableConfigKeys.committed);
            this.selectedFormulaItems = this.getSelectedCustomAvailableBudgetFormulaItems();
          });
      }

      if (shouldDisableCommitted) {
        setTimeout(() => {
            committed.disabled = true;
        });
      }

      // --- Rule 4: If Planned is not selected anymore → allow toggling Committed
      if (committed && !isPlanned) {
        committed.disabled = false;
      }
    }
    else{
      if (isTotal) {
        // Committed & Planned never appear with Total
        if (committed) { committed.value = false; committed.disabled = true; }
        if (planned)   { planned.value   = false; planned.disabled   = true; }

        // Keep Forecast toggleable so user can go to [Total, Actual]
        if (forecast) forecast.disabled = false;

        if (!isForecast) {
          // Must be [Total, Actual]
          if (total) { total.value = true; total.disabled = true; }
          if (actual) { actual.value = true; actual.disabled = true; }
        } else {
          // [Total, Forecast] or [Total, Forecast, Actual] → Actual can toggle
          if (actual) actual.disabled = false;
          // Forecast already enabled above
          if(!isActual){
            total.disabled = true;
            forecast.disabled = true;
          }
          else{
            total.disabled = false;
            forecast.disabled = false;
          }
        }
      } else {
        // No Total → all valid patterns require Forecast & Actual
        if (forecast) { forecast.value = true; forecast.disabled = true; }
        if (actual)   { actual.value   = true; actual.disabled   = true; }

        // Committed must be selectable
        if (committed) committed.disabled = false;
        // Planned must be selectable
        if (planned) planned.disabled = false;

        // If user selects Planned first, auto-select Committed
        if (planned) {
          if (planned.value) {
            committed.value = true;
            isCommitted = true;
            setTimeout(() => {
              committed.disabled = true;
            });
          }
          if (!isCommitted) planned.value = false;
        }
      }

      // Safety: Planned cannot be on if Committed is off
      if (planned && planned.value && !isCommitted) {
        planned.value = false;
      }

      // Safety: enforce [Forecast, Actual] baseline when no Total and either is missing
      if (!isTotal && (!isForecast || !isActual)) {
        if (forecast) { forecast.value = true; forecast.disabled = true; }
        if (actual)   { actual.value   = true; actual.disabled   = true; }
      }
    }
    
    this.selectedFormulaItems = this.getSelectedCustomAvailableBudgetFormulaItems();
  }

  getSelectedCustomAvailableBudgetFormulaItems(): string[] {
    return this.data.config.columns.filter(col => col.type === TableConfigActionType.checkbox && col.visible && col.value).map(col => col.label); 
  }

  getSelectedRowItems(): string[] {
    return this.data.config.segmentBreakdownData.filter(col => col.visible && col.value).map(col => col.key); 
  }

  checkManagePageFormulaSameAsBudgetFormulaOnApply(): void {
    const selectedFormulaItems = this.getSelectedCustomAvailableBudgetFormulaItems();
    const formula = this.data.config.columns.find(item => item.key === TableConfigKeys.columnsFormula);
    const areBudgetFormulaAndManagePageCABFormulasEqual = this.areBudgetFormulaAndManagePageSelectedCABFormulasEqual();
    
    if(!areBudgetFormulaAndManagePageCABFormulasEqual) {
      this.data.config.columnsFormula = true;
      formula.value = true;
      this.customAvailableBudgetConfigurationService.setManagePageCustomAvailableBudgetFormulaOverride(true);
    } else {
      this.data.config.columnsFormula = false;
      formula.value = false;
      this.customAvailableBudgetConfigurationService.setManagePageCustomAvailableBudgetFormulaOverride(false);
      
    }
    this.customAvailableBudgetConfigurationService.setManagePageCustomAvailableBudgetFormulaItems(selectedFormulaItems);
  }

  areBudgetFormulaAndManagePageSelectedCABFormulasEqual(): boolean {
    const selectedFormulaItems = this.getSelectedCustomAvailableBudgetFormulaItems();
    const budgetFormulaItems = this.customAvailableBudgetConfigurationService.getBudgetDefinedCABFormulaItemsLabel();
    return this.utilityService.areEqualSets(selectedFormulaItems, budgetFormulaItems);
  }

  onReset() {
    let defaultViewConfig = defaultViewConfigForCAB;
    if(this.data.isForecastSmartCell){
      defaultViewConfig.columns.budget = false;
    }
    const budgetLevelFormulaViewConfig = this.customAvailableBudgetConfigurationService.transformDefaultTableConfigDataFromAPIToDialogState(defaultViewConfig);
    this.data.config[ManageTableViewConfigSections.columns].forEach((item) => {
      if (item.type === TableConfigActionType.checkbox) {
       item.value = budgetLevelFormulaViewConfig.columns[item.key] || false;
      }
    });

    this.data.config.columnsFormula = false;
    this.selectedFormulaItems =  this.getSelectedCustomAvailableBudgetFormulaItems();
    this.disableResetForCAB = true;
    this.checkIfManagePageFormulaChangedInConfigureTable();
    this.handleDisabledChecboxStateForCustomAvailableBudgetTableConfigDialog(true);
  }

  checkIfManagePageFormulaChangedInConfigureTable(): void {  
    const hasManagPageColumnsFormulaChanged = !this.utilityService.areEqualSets(this.selectedFormulaItems, this.lastSavedSelectedColumnFormulaItems);
    const hasManagePageRowsFormulaChanged = !this.utilityService.areEqualSets(this.selectedRowItems, this.lastSavedSelectedRowItems);
    this.hasManagePageFormulaChanged = hasManagPageColumnsFormulaChanged || hasManagePageRowsFormulaChanged;
  }

  
  ngOnDestroy(): void {
    this.destroy$.complete();
  }

}
